
  import { defineComponent, ref, computed, watch } from 'vue';
  import { Loader } from '@/ui/components/Loader';
  import { Box } from '@/ui/components/Box';
  import { PageTitle } from '@/ui/components/PageTitle';
  import { Button } from '@/ui/components/Button';
  import { InputField } from '@/ui/components/form/InputField';
  import { Pagination } from '@/ui/components/Pagination';
  import Level from '@/ui/components/common/Level.vue';
  import { CreateReportModal } from '@/ui/components/modals/CreateReportModal';
  import SelectReportTemplateModal from '@/ui/components/modals/SelectReportTemplateModal.vue';
  import AnalyticsEmptyPage from '@/ui/pages/analytics/Builder/AnalyticsEmptyPage.vue';
  import { router } from '@/ui/routes';
  import { t, du } from '@/ui/i18n/i18n';
  import * as qs from '@/ui/queryState';
  import { usePagination, useSorting, useTask } from '@/ui/concerns';
  import { SortDirection } from '@/ui/types';
  import { emptyList } from '@/services/api';
  import { ReportRecordList } from '@/services/api/resources/analytics-builder/model';
  import { list as getReportList, bulkDelete, remove } from '@/services/api/resources/analytics-builder/actions';
  import { useBulkDeletable } from '@/ui/pages/useBulkDeletable';
  import BulkDeleteButton from '@/ui/components/button/BulkDeleteButton.vue';
  import { BulkDeletableTable } from '@/ui/components/table/BulkDeletableTable';
  import { updateMultiple } from '@/ui/queryState';
  import { hasFeature, hasPermission, Permissions } from '@/ui/auth/auth';
  import { isAiChatEnabled } from '@/ui/helpers/featureHelper';
  import { ForbiddenPage } from '@/ui/pages/error/ForbiddenPage';
  import { ReportPreMadeTemplate } from '@/services/api/resources/analytics-builder/constants';
  import { useReportTemplateStore } from '@/stores/analytics/reportTemplate';
  import { useReportRecord } from '@/ui/concerns/analytics/useReportRecord';
  import { useReportTemplate } from '@/ui/concerns/analytics/useReportTemplate';
  import ReportExport from '@/ui/components/analytics/ReportExport.vue';
  import { SupportedExportFormat } from '@/services/api/resources/analytics';
  import { useExports } from '@/ui/concerns/analytics/exports/useExports';
  import { ResourceTeaserPage } from '@/ui/pages/teasers/ResourceTeaserPage';

  export default defineComponent({
    components: {
      ForbiddenPage,
      InputField,
      Pagination,
      BulkDeletableTable,
      BulkDeleteButton,
      AnalyticsEmptyPage,
      CreateReportModal,
      Button,
      PageTitle,
      Loader,
      Level,
      Box,
      SelectReportTemplateModal,
      ReportExport,
      ResourceTeaserPage,
    },
    setup() {
      const store = useReportTemplateStore();
      const { isReportExportLoading, isBulkReportExportLoading, exportReport, bulkExportReport } = useExports();
      const { createTemplateReport, createResultSummaryTemplateReport } = useReportRecord();
      const { isLoadingFormItems, getFormItems, formItems, formItemsRequestError } = useReportTemplate();
      const initialLoading = ref(true);
      const isModalOpen = ref(false);
      const isModalFormTemplateOpen = ref(false);
      const list = ref<ReportRecordList>(emptyList());
      const selectedFormTemplate = ref<{ id: number; name: string} | null>(null);

      const search = qs.string('search', '');
      const { page, perPage, resetPagination } = usePagination();
      const { sort, dir } = useSorting('createdAt', SortDirection.Descending);

      const sortReports = (field: string, direction: SortDirection) => qs.updateMultiple(() => {
        resetPagination();
        sort.value = field;
        dir.value = direction;
      });

      const updateSearch = (value: string) => updateMultiple(() => {
        resetPagination();
        search.value = value;
      }, true);

      const getList = useTask(
        () => getReportList({
          page: page.value,
          perPage: perPage.value,
          search: search.value,
          sort: sort.value,
          dir: dir.value,
        }),
        reportList => {
          list.value = reportList;
          initialLoading.value = false;
        },
      );

      watch([page, perPage, search, sort, dir], getList.run);

      const { selectedResources, deleteBulkTask, onCheckedRowsUpdate } = useBulkDeletable({
        getList,
        bulkDeleteFn: bulkDelete,
      });
      const isDeleteBulkTaskPending = computed(() => deleteBulkTask.state.value.pending);

      const isRowCheckable = (row: BaseEntity) => row.authorizedTo.delete;

      const openModal = () => {
        isModalOpen.value = true;
      };

      const closeModal = () => {
        isModalOpen.value = false;
      };

      const closeFormTemplateModal = () => {
        isModalFormTemplateOpen.value = false;
      };

      const createReportFromScratch = () => {
        closeModal();
        router.push({ name: 'analyticsReportCreate' });
      };

      const createReportFromFormTemplate = (id: number, name: string) => {
        selectedFormTemplate.value = { id, name };
        closeFormTemplateModal();
        isLoadingFormItems.value = true;
        getFormItems.run(id);
      };

      const createReportFromTemplate = (templateType: ReportPreMadeTemplate) => {
        closeModal();

        if (templateType !== ReportPreMadeTemplate.resultSummary) {
          store.setTemplate(templateType, createTemplateReport(templateType));
          router.push({ name: 'analyticsReportCreate' });
          return;
        }

        isModalFormTemplateOpen.value = true;
      };

      const isExportLoading = computed(() => isReportExportLoading.value || isBulkReportExportLoading.value);
      const disableExport = computed(() => selectedResources.value.length === 0);

      const onExport = (format: SupportedExportFormat) => {
        if (!selectedResources.value?.length) {
          return;
        }

        if (selectedResources.value.length === 1) {
          exportReport(selectedResources.value[0].id, format).run();
          return;
        }

        bulkExportReport(selectedResources.value.map(report => report.id), format).run();
      };

      watch([formItems, formItemsRequestError], () => {
        if (formItemsRequestError.value || !selectedFormTemplate.value) {
          return;
        }

        store.setTemplate(
          ReportPreMadeTemplate.resultSummary,
          createResultSummaryTemplateReport(
            selectedFormTemplate.value.id,
            selectedFormTemplate.value.name,
            formItems.value,
          ),
        );
        router.push({ name: 'analyticsReportCreate' });
      });

      const getListTask = computed(() => getList);

      const showEmptyPage = computed(() => search.value === '' && list.value.meta.total === 0);

      const pageTitleText = t('common.analytics') as string;

      const canAccessIndexPage = hasPermission(Permissions.VIEW_ALL_ANALYTICS)
        || hasPermission(Permissions.MANAGE_ALL_ANALYTICS)
        || hasPermission(Permissions.MANAGE_OWN_ANALYTICS);

      const hasAnalyticsFeature = hasFeature('analytics');

      const openAiChat = () => {
        router.push({ name: 'analyticsAiChat' });
      };

      const isAiChatFeatureEnabled = computed(() => isAiChatEnabled());

      return {
        isLoadingFormItems,
        isModalOpen,
        isModalFormTemplateOpen,
        closeModal,
        openModal,
        openAiChat,
        closeFormTemplateModal,
        createReportFromScratch,
        createReportFromFormTemplate,
        createReportFromTemplate,
        showEmptyPage,
        pageTitleText,
        initialLoading,
        search,
        updateSearch,
        isRowCheckable,
        sortReports,
        page,
        perPage,
        getListTask,
        list,
        selectedResources,
        onCheckedRowsUpdate,
        isDeleteBulkTaskPending,
        removeReport: remove,
        sort,
        dir,
        du,
        canAccessIndexPage,
        isExportLoading,
        disableExport,
        onExport,
        hasAnalyticsFeature,
        isAiChatFeatureEnabled,
      };
    },
  });
